import React, { useMemo } from 'react'
import { type GridColumns } from '@retailer-platform/shared-components'
import { useDomainMessages } from 'domains/products/utils/domain/intl'
import { AttributeOverridesTableAttributeNameCell } from 'domains/products/components/view/components/attribute-overrides/components/attribute-name-cell/AttributeOverridesTableAttributeNameCell'
import { AttributeOverridesTableCreatedByCell } from 'domains/products/components/view/components/attribute-overrides/components/created-by-cell/AttributeOverridesTableCreatedByCell'
import { AttributeOverridesTableExpiryDateCell } from 'domains/products/components/view/components/attribute-overrides/components/expiry-date-cell/AttributeOverridesTableExpiryDateCell'
import { AttributeOverridesTableReasonCell } from 'domains/products/components/view/components/attribute-overrides/components/reason-cell/AttributeOverridesTableReasonCell'
import { AttributeOverridesTableOverrideTypeCell } from 'domains/products/components/view/components/attribute-overrides/components/override-type-cell/AttributeOverridesTableOverrideTypeCell'
import { AttributeOverridesTableSourceTypeCell } from 'domains/products/components/view/components/attribute-overrides/components/source-type-cell/AttributeOverridesTableSourceTypeCell'
import { AttributeOverridesTableTicketUrlCell } from 'domains/products/components/view/components/attribute-overrides/components/ticket-url-cell/AttributeOverridesTableTicketUrlCell'
import { AttributeOverridesTableValueCell } from 'domains/products/components/view/components/attribute-overrides/components/value-cell/AttributeOverridesTableValueCell'
import { type ValueBasedOverride } from 'domains/products/components/view/types/attributeOverride.types'

export const useAttributeOverridesColumns = () => {
  const columnHeaders = useDomainMessages({
    attributeName: 'productsDomain.view.tabs.attributeOverrides.list.column.attributeName',
    createdBy: 'productsDomain.view.tabs.attributeOverrides.list.column.createdBy',
    reason: 'productsDomain.view.tabs.attributeOverrides.list.column.reason',
    expiryDate: 'productsDomain.view.tabs.attributeOverrides.list.column.expiryDate',
    overrideType: 'productsDomain.view.tabs.attributeOverrides.list.column.overrideType',
    overrideValue: 'productsDomain.view.tabs.attributeOverrides.list.column.overrideValue',
    sourceType: 'productsDomain.view.tabs.attributeOverrides.list.column.sourceType',
    sourceValue: 'productsDomain.view.tabs.attributeOverrides.list.column.sourceValue',
    ticketUrl: 'productsDomain.view.tabs.attributeOverrides.list.column.ticketUrl',
  })

  const columns = useMemo(() => {
    const columns: GridColumns<ValueBasedOverride> = [
      {
        id: 'attributeName',
        Header: columnHeaders.attributeName,
        Cell: ({ row }) => (
          <AttributeOverridesTableAttributeNameCell attributeName={row.original.attributeName} />
        ),
        width: 35,
      },
      {
        id: 'overrideType',
        Header: columnHeaders.overrideType,
        Cell: ({ row }) => (
          <AttributeOverridesTableOverrideTypeCell overrideType={row.original.overrideType} />
        ),
        width: 30,
      },
      {
        id: 'sourceType',
        Header: columnHeaders.sourceType,
        Cell: ({ row }) => (
          <AttributeOverridesTableSourceTypeCell sourceType={row.original.sourceType} />
        ),
        width: 60,
      },
      {
        id: 'sourceValue',
        Header: columnHeaders.sourceValue,
        Cell: ({ row }) => (
          <AttributeOverridesTableValueCell displayValue={row.original.sourceValue} />
        ),
        width: 42,
      },
      {
        id: 'overrideValue',
        Header: columnHeaders.overrideValue,
        Cell: ({ row }) => (
          <AttributeOverridesTableValueCell displayValue={row.original.overrideValue} />
        ),
        width: 42,
      },
      {
        id: 'createdBy',
        Header: columnHeaders.createdBy,
        Cell: ({ row }) => (
          <AttributeOverridesTableCreatedByCell username={row.original.username} />
        ),
        width: 42,
      },
      {
        id: 'expiryDate',
        Header: columnHeaders.expiryDate,
        Cell: ({ row }) => (
          <AttributeOverridesTableExpiryDateCell expiryDate={row.original.expiryDate} />
        ),
        width: 30,
      },
      {
        id: 'reason',
        Header: columnHeaders.reason,
        Cell: ({ row }) => <AttributeOverridesTableReasonCell reason={row.original.createdWhy} />,
        width: 42,
      },
      {
        id: 'ticketUrl',
        Header: columnHeaders.ticketUrl,
        Cell: ({ row }) => (
          <AttributeOverridesTableTicketUrlCell ticketUrl={row.original.ticketUrl} />
        ),
        width: 42,
      },
    ]

    return columns
  }, [columnHeaders])

  return columns
}
